import { Card, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { UnassignedFolderGroup } from "./unassigned-folder-group"
import { NothingLeftToAssign } from "./nothing-left-to-assign"
import { allSelectionState } from "./board-selection"
import { SelectionActionBar } from "./selection-action-bar"
import { ASSIGNMENT_SEARCH_PARAM } from "./assignment-search-param"

/**
 * Left pane of the assignment board: schedules still routed by skill, grouped by library
 * folder. It shrinks as schedules are assigned, so it doubles as a to-do list.
 */
export function UnassignedSchedulesPane() {
    const { board, selection, selectedCount, readOnly, onToggleAll } = useBoundContext()
    const allState = allSelectionState(selection, board.groups)

    return (
        // overflow visible: Card clips by default, which would stop the selection bar sticking
        <Card variant="outlined" data-cy="unassigned-schedules-pane" sx={{ overflow: "visible" }}>
            {/* Kept to two rows so the list starts high on the page: the title with how to use
                it, then Select all (in the same column as the folder tick boxes) beside search */}
            <Stack spacing={0.5} sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
                    <Typography variant="subtitle2">Not yet assigned (routed by skill)</Typography>
                    <If value={!readOnly}>
                        <Typography variant="caption" color="text.secondary">
                            Tick schedules or a whole folder, then click a trade, or drag them onto one.
                        </Typography>
                    </If>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <If value={!readOnly && board.groups.length > 0}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allState === "all"}
                                    indeterminate={allState === "some"}
                                    onChange={onToggleAll}
                                    data-cy="assignment-select-all"
                                />
                            }
                            label={<Typography variant="body2">Select all</Typography>}
                            sx={{ m: 0, flexShrink: 0 }}
                        />
                    </If>
                    {/* Its own parameter: a search typed on the skill routing view must not carry over */}
                    <SearchBoxUsingSearchParam
                        param={ASSIGNMENT_SEARCH_PARAM}
                        autoFocus={false}
                        sx={{ py: 0, m: 0, flex: 1, minWidth: 0 }}
                    />
                </Stack>
            </Stack>
            <Repeat
                list={board.groups}
                keyFn={groupKey}
                pass="group"
                item={<UnassignedFolderGroup />}
                fallback={<NothingLeftToAssign />}
            />
            <If value={!readOnly && selectedCount > 0}>
                <SelectionActionBar />
            </If>
        </Card>
    )
}

function groupKey(group) {
    return group.key
}
